import { ArrowForward } from '@mui/icons-material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PinDropIcon from '@mui/icons-material/PinDrop'
import { Avatar, Box, Typography } from '@mui/material'
import { FormLabel, MButton } from 'components'
import { color } from 'config/color'
import dayjs from 'dayjs'
import type React from 'react'
import { useState } from 'react'
import { APIs, type IServiceRequest } from 'services'
import { parseAddress } from 'utils/helpers/address'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { FormType, type InformationFormType } from './ServiceForm'
import { AccountType } from './InformationForm'

interface ReviewInformationFormProps {
    handleNext: () => void
    handleBack: () => void
    formData: InformationFormType | undefined
    setActiveStep: (step: number) => void
    formType: string
    setFormStatus: (status: boolean) => void
}


const ReviewInformationForm: React.FC<ReviewInformationFormProps> = (props: ReviewInformationFormProps) => {
    const isMobile = useMobileBreakpoints()
    const { handleBack, formData, formType, setFormStatus, handleNext } = props
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const MAILING_ADDRESS = 'MAILING_ADDRESS'

    const mailingAddress = `${formData?.mailingAddressLine1}, ${formData?.mailingAddressLine2 !== '' ? formData?.mailingAddressLine2 : ''} ${formData?.mailingCity}, ${formData?.mailingState}, ${formData?.mailingZipCode}`
    const selectedAddressConcat = `${formData?.svcAddressLine1}, ${formData?.svcAddressLine2 !== '' ? formData?.svcAddressLine2 : ''} ${formData?.svcAddressCity}, ${formData?.svcAddressState}, ${formData?.svcAddressZipCode}`

    const formatDate = (date: string | Date | dayjs.Dayjs | undefined) => date ? dayjs(date).format('YYYY-MM-DD') : undefined

    const handleSubmit = async (): Promise<void> => {
        setIsLoading(true)

        const { address1, address2, city, state, zipCode } = parseAddress(formData?.selectedAddress ?? '')
        const request: IServiceRequest = {
            name: formData?.name,
            mobile: formData?.phone,
            email: formData?.email,
            requestType: (formType.toUpperCase() === 'NEW TAP' ? 'NEW_TAP' : formType.toUpperCase()),
            requestDate: dayjs().format('YYYY-MM-DD'),
            comments: formData?.comments,

            ...((formType === FormType.Stop) && {
                currentServiceAddress: {
                    address1: (address1 !== '' ? address1 : formData?.svcAddressLine1),
                    address2: (address2 !== '' ? address2 : formData?.svcAddressLine2) ?? '',
                    city: (city !== '' ? city : formData?.svcAddressCity),
                    state: (state !== '' ? state : formData?.svcAddressState),
                    zipCode: (zipCode !== '' ? zipCode : formData?.svcAddressZipCode)
                },
                stopDate: formatDate(formData?.stopDate)
            }),

            ...([FormType.Start, FormType.NewTap].includes(formType as FormType) && {
                newServiceAddress: {
                    address1: (address1 !== '' ? address1 : formData?.svcAddressLine1),
                    address2: (address2 !== '' ? address2 : formData?.svcAddressLine2) ?? '',
                    city: (city !== '' ? city : formData?.svcAddressCity),
                    state: (state !== '' ? state : formData?.svcAddressState),
                    zipCode: (zipCode !== '' ? zipCode : formData?.svcAddressZipCode)
                }
            }),

            ...((formType === FormType.Transfer) && {
                currentServiceAddress: {
                    address1: formData?.svcAddressLine1,
                    address2: (formData?.svcAddressLine2 !== '') ? formData?.svcAddressLine2 : formData?.svcAddressLine2 ?? '',
                    city: formData?.svcAddressCity,
                    state: formData?.svcAddressState,
                    zipCode: formData?.svcAddressZipCode
                },
                newServiceAddress: {
                    address1,
                    address2: (address2 !== '' ? address2 : '') ?? '',
                    city,
                    state,
                    zipCode
                },
                startDate: formatDate(formData?.startDate),
                stopDate: formatDate(formData?.stopDate)
            }),

            ...((formType === FormType.Start || formType === FormType.Transfer) && {
                accountType: formData?.accountType,
                ownershipType: formData?.ownershipType,
                billPref: formData?.billingPref,
                mailingPref: formData?.mailingPref,
                birthDate: dayjs(formData?.birthDate).format('MM/DD/YYYY'),
                mailingAddress: {
                    address1: formData?.mailingPref === MAILING_ADDRESS ? formData?.mailingAddressLine1 : address1,
                    address2: formData?.mailingPref === MAILING_ADDRESS ? formData?.mailingAddressLine2 : address2 ?? '',
                    city: formData?.mailingPref === MAILING_ADDRESS ? formData?.mailingCity : city,
                    state: formData?.mailingPref === MAILING_ADDRESS ? formData?.mailingState : state,
                    zipCode: formData?.mailingPref === MAILING_ADDRESS ? formData?.mailingZipCode : zipCode
                }
            }),

            ...(formType === FormType.Start && {
                startDate: formatDate(formData?.startDate)
            }),
            ...((formType === FormType.NewTap || formType === FormType.Stop) && {
                mailingAddress: {
                    address1: formData?.mailingAddressLine1,
                    address2: formData?.mailingAddressLine2 ?? '',
                    city: formData?.mailingCity,
                    state: formData?.mailingState,
                    zipCode: formData?.mailingZipCode
                }
            }),
            ...((formData?.accountType === AccountType.Residential) && {
                driversLicense: formData?.driversLicense,
                state: formData?.state
            }),
            ...((formData?.accountType === AccountType.Commercial) && {
                ein: formData?.ein
            })
        }


        try {
            const response = await APIs.sendServiceRequest(request)
            if (response?.output?.status === 'Success') {
                setFormStatus(true)
                console.log(response)
            }
        } catch (error) {
            setFormStatus(false)
            console.log(error)
        } finally {
            handleNext()
            setIsLoading(false)
        }
    }


    return (
        <>
            <Box border={`solid 1px ${color.background[100]}`} borderRadius={1}>
                <Box p={3} bgcolor={color.grey[50]} borderBottom={`solid 1px ${color.background[100]}`}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box>
                            <FormLabel>SERVICE REQUESTED:</FormLabel>
                            <Typography fontSize={24} fontWeight={500}>{`${formType} Service`}</Typography>
                        </Box>
                        {/* <MButton variant="outlined" texttransform="none" rounded="true">Download PDF</MButton> */}
                    </Box>
                    <Box display="flex" gap={2}>
                        {(formType === FormType.Start || formType === FormType.Stop) && <Box p={1} flex={1} display="flex" bgcolor={color.white} border={`solid 1px ${color.background[100]}`} borderRadius={1} justifyContent="space-between">
                            <Box display="flex">
                                <Avatar sx={{ bgcolor: color.primary[50] }} variant="rounded">
                                    <CalendarTodayIcon sx={{ color: color.primary[600] }} />
                                </Avatar>
                                <Box ml={2}>
                                    {(formType === FormType.Start) && <>
                                        <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400}>START DATE</Typography>
                                        <Typography fontSize={16}>{dayjs(formData?.startDate).format('YYYY/MM/DD')}</Typography>
                                    </>}
                                    {(formType === FormType.Stop) && <>
                                        <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400}>STOP DATE</Typography>
                                        <Typography fontSize={16}>{dayjs(formData?.stopDate).format('YYYY/MM/DD')}</Typography>
                                    </>}
                                </Box >
                            </Box >

                            {/* <Link sx={{ cursor: 'pointer' }} underline="none" fontSize={16} onClick={() => { setActiveStep(3) }}>Edit</Link> */}
                        </Box >}
                        {formType !== FormType.Transfer && <Box p={1} flex={2} display="flex" bgcolor={color.white} border={`solid 1px ${color.background[100]}`} borderRadius={1} justifyContent="space-between">
                            <Box display="flex">
                                <Avatar sx={{ bgcolor: color.primary[50] }} variant="rounded">
                                    <PinDropIcon sx={{ color: color.primary[600] }} />
                                </Avatar>
                                <Box ml={2}>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400}>ADDRESS</Typography>
                                    <Typography fontSize={16}>{formData?.selectedAddress !== '' ? formData?.selectedAddress : selectedAddressConcat !== '' ? selectedAddressConcat : ''}</Typography>
                                </Box>
                            </Box>
                            {/* <Link sx={{ cursor: 'pointer' }} underline="none" fontSize={16} onClick={() => { setActiveStep(2) }}>Edit</Link> */}
                        </Box>}
                        {formType === FormType.Transfer && <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                            <Box flex={1} bgcolor={color.white} p={2} border={`solid 1px ${color.background[100]}`} borderRadius={1}>
                                <Typography fontSize={18} fontWeight={500} mb={2}>Transfer out from:</Typography>
                                <Box mb={1}>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Address</Typography>
                                    <Typography textTransform="capitalize" fontSize={16}>{selectedAddressConcat}</Typography>
                                </Box>
                                <Box>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Stop Date</Typography>
                                    <Typography textTransform="capitalize" fontSize={16}>{dayjs(formData?.stopDate).format('YYYY/MM/DD')}</Typography>
                                </Box>
                            </Box>
                            <Avatar sx={{
                                bgcolor: color.grey[100],
                                border: `solid 1px ${color.grey[300]}`,
                                width: 60,
                                height: 60,
                                mx: 1
                            }} >
                                <ArrowForward sx={{ color: color.grey[400], fontSize: 40 }} />
                            </Avatar>


                            <Box flex={1} bgcolor={color.white} p={2} border={`solid 1px ${color.background[100]}`} borderRadius={1} >
                                <Typography fontSize={18} fontWeight={500} mb={2}>Transfer in to:</Typography>
                                <Box mb={1}>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Address</Typography>
                                    <Typography textTransform="capitalize" fontSize={16}>{formData?.selectedAddress}</Typography>

                                </Box>
                                <Box>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Stop Date</Typography>
                                    <Typography textTransform="capitalize" fontSize={16}>{dayjs(formData?.startDate).format('YYYY/MM/DD')}</Typography>
                                </Box>
                            </Box>

                        </Box>}
                    </Box >


                </Box >
                <Box m={3}>
                    {formType !== FormType.NewTap && <Box py={2} my={2} borderBottom={`solid 1px ${color.background[100]}`}>
                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography fontSize={18} fontWeight={500}>Service Information</Typography>
                            {/* <Link sx={{ cursor: 'pointer' }} underline="none" fontSize={16} onClick={() => { setActiveStep(3) }}>Edit</Link> */}
                        </Box>
                        <Box display="flex" gap={10}>
                            <Box>
                                <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Requestor Name</Typography>
                                <Typography fontSize={16}>{formData?.name}</Typography>
                            </Box >
                            {(formType === FormType.Start || formType === FormType.Transfer) && <>   <Box>
                                <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Account type</Typography>
                                <Typography textTransform="capitalize" fontSize={16}>{formData?.accountType}</Typography>
                            </Box>
                                <Box>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Ownership type</Typography>
                                    <Typography textTransform="capitalize" fontSize={16}>{formData?.ownershipType}</Typography>
                                </Box>
                            </>}
                        </Box >
                    </Box >}
                    <Box py={2} my={2} borderBottom={`solid 1px ${color.background[100]}`}>
                        <Typography fontSize={18} fontWeight={500} mb={2}>Personal Information</Typography>
                        {formType === FormType.NewTap && <Box mb={2}>
                            <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Customer Name</Typography>
                            <Typography fontSize={16}>{formData?.name}</Typography>
                        </Box>}
                        <Box display="flex" gap={10}>
                            {(formData?.birthDate !== '') && <Box>
                                <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Date of birth</Typography>
                                <Typography fontSize={16}>{dayjs(formData?.birthDate).format('YYYY/MM/DD')}</Typography>
                            </Box>}
                            <Box>
                                <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Phone Number</Typography>
                                <Typography fontSize={16}>{formData?.phone}</Typography>
                            </Box>
                            <Box>
                                <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Email address</Typography>
                                <Typography fontSize={16}>{formData?.email}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    {
                        (formType === FormType.Start || formType === FormType.Transfer) && <Box>
                            <Typography fontSize={18} fontWeight={500} mb={2}>Preferences</Typography>
                            <Box display="flex" gap={10}>
                                <Box>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Billing</Typography>
                                    <Typography textTransform="capitalize" fontSize={16}>{formData?.billingPref}</Typography>
                                </Box>
                                <Box>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Mailing</Typography>
                                    <Typography fontSize={16}>{formData?.mailingPref === MAILING_ADDRESS ? mailingAddress : formData?.selectedAddress}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    }
                    {
                        (formType === FormType.Stop || formType === FormType.NewTap) && <Box>
                            <Typography fontSize={18} fontWeight={500} mb={2}>Forwarding Address</Typography>
                            <Box display="flex" gap={10}>
                                <Box>
                                    <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Mailing Address</Typography>
                                    <Typography fontSize={16}>{mailingAddress}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    }
                    {
                        ((formData?.comments) !== '') && <Box my={2}>
                            <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Comments</Typography>
                            <Typography textTransform="capitalize" fontSize={16}>{formData?.comments}</Typography>
                        </Box>
                    }

                </Box >

            </Box >
            <Box
                display="flex"
                my={2}
                width={isMobile ? '100%' : '50%'}
                flexDirection={isMobile ? 'column' : 'row'}
                gap={2}
            >
                <MButton
                    fullWidth
                    variant="outlined"
                    texttransform="none"
                    rounded="true"
                    onClick={() => {
                        handleBack()
                    }}
                >
                    Back
                </MButton>
                <MButton
                    fullWidth
                    variant="contained"
                    type="submit"
                    texttransform="none"
                    rounded="true"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    loading={isLoading}
                >
                    Submit Request
                </MButton>
            </Box>
        </>
    )
}

export default ReviewInformationForm
